import React, { useState } from 'react'
import { ControlledTextArea, ControlledTextField } from 'transfix-form/components'
import { Button } from 'transfix-ui/components/Button'
import { Divider, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { FP_HOME } from 'apps/fleet-planner/config'
import { useActivationContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { INLINE_MESSAGE_VARIANTS, InlineMessage } from 'transfix-ui'
import { postMessageToMobileApp } from 'utils/mobileApp'
import { getErrorMessage } from './ProvideSafetyLetterSchema'
import { useCreateSafetyLetterMutation } from './graphql/CreateSafetyLetter'
import { useProvideSafetyLetter } from './useProvideSafetyLetter'

const useProvideSafetyLetterStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(1.5),
  },
  smGap: {
    marginTop: theme.spacing(1),
  },
  mdGap: {
    marginTop: theme.spacing(2),
  },
  lgGap: {
    marginTop: theme.spacing(3),
  },
  fieldBox: {
    marginTop: theme.spacing(4),
  },
  divider: {
    margin: theme.spacing(4, 0),
  },
  list: {
    marginLeft: theme.spacing(0.5),
  },
}))

export interface IProvideSafetyLetterProps {}

const ProvideSafetyLetter = () => {
  const { trackEvent } = useFMSAnalytics()
  const styles = useProvideSafetyLetterStyles()
  const { carrierDetails, mobileApp } = useActivationContext()
  const { fleetPlannerMotorCarrierId, legalName } = carrierDetails || {}
  const { control, errors, handleSubmit } = useProvideSafetyLetter({})
  const [createSafetyLetter] = useCreateSafetyLetterMutation()
  const [errorMessage, setErrorMessage] = useState<string>('')

  const onSubmitError = () => {
    setErrorMessage(`Something went wrong, please check your information and try submitting again`)
  }

  const returnToHomepage = (success?: boolean) => {
    const toastParam = success ? '?safetyLetter=true' : ''
    window.location.replace(`${__ROOT__}/fms${FP_HOME}${toastParam}`)
  }

  const skipHandler = () => {
    trackEvent('Setup Guide', 'SKIPPED_UPLOAD_DOCUMENTS')
    postMessageToMobileApp(mobileApp, 'SKIPPED_DOCUMENT_UPLOAD')
    returnToHomepage()
  }
  const onSubmitSuccess = async () => {
    try {
      const { driverRating, jobTitle, signatory, vehicleRating } = control.getValues()
      setErrorMessage('')
      const { data } = await createSafetyLetter({
        variables: {
          input: {
            driverRatingSummary: driverRating,
            motorCarrierId: fleetPlannerMotorCarrierId,
            safetyLetterSignatory: signatory,
            safetyLetterSignatoryTitle: jobTitle,
            vehicleRatingSummary: vehicleRating,
          },
        },
      })
      postMessageToMobileApp(mobileApp, 'DOCUMENT_UPLOAD_COMPLETE')
      trackEvent('Setup Guide', 'PROVIDE_SAFETY_LETTER_SUCCESS', { data })
      returnToHomepage(true)
    } catch (error) {
      trackEvent('Setup Guide', 'PROVIDE_SAFETY_LETTER_FAILURE', { errors })
      onSubmitError()
    }
  }

  const submitHandler = handleSubmit(onSubmitSuccess)

  return (
    <form onSubmit={submitHandler} data-test='provide-safety-letter'>
      <div className={styles.container}>
        <div className={styles.fieldBox}>
          <Grid container alignItems='center' justifyContent='center'>
            <Grid item xs={12} md={8} lg={6}>
              <Typography variant='h3'>Provide Safety Summary</Typography>
              <div className={styles.smGap}>
                <Typography variant='body1'>
                  It looks like {legalName} has a safety and/or out of service violation scores
                  above the national average. In order to complete your packet, Transfix Compliance
                  is requesting a Safety Summary outlining the actions you are taking to prevent
                  future violations. Please provide responses to the following:
                </Typography>
              </div>
            </Grid>
          </Grid>
        </div>

        <div className={styles.fieldBox}>
          <Grid container justifyContent='center'>
            <Grid item xs={12} md={8} lg={6}>
              <Typography variant='subtitle1'>Driver Rating</Typography>
              <div className={styles.list}>
                <Typography variant='body1'>
                  1. What are you doing to make sure your drivers are aware of standard safety
                  regulations?
                </Typography>
                <Typography variant='body1'>
                  2. What training or re-training policies do you have in place for drivers who are
                  found in violation?
                </Typography>
              </div>
            </Grid>
          </Grid>
        </div>

        <div className={styles.fieldBox}>
          <Grid container justifyContent='center'>
            <Grid item xs={12} md={8} lg={6}>
              <ControlledTextArea
                control={control}
                name='driverRating'
                TextAreaProps={{
                  required: true,
                  label: (
                    <Typography style={{ fontWeight: 600 }} component='span'>
                      Driver Rating Summary
                    </Typography>
                  ),
                  placeholder: 'Provide your response',
                  invalid: !!getErrorMessage(errors, 'driverRating'),
                }}
              />
            </Grid>
          </Grid>
        </div>

        <div className={styles.divider}>
          <Grid container justifyContent='center'>
            <Grid item xs={12} md={8} lg={6}>
              <Divider />
            </Grid>
          </Grid>
        </div>

        <div className={styles.fieldBox}>
          <Grid container justifyContent='center'>
            <Grid item xs={12} md={8} lg={6}>
              <Typography variant='subtitle1'>Vehicle Rating</Typography>
              <div className={styles.list}>
                <Typography variant='body1'>
                  1. What you are doing to make sure your trucks and trailers are in good working
                  order?
                </Typography>
              </div>
            </Grid>
          </Grid>
        </div>

        <div className={styles.fieldBox}>
          <Grid container justifyContent='center'>
            <Grid item xs={12} md={8} lg={6}>
              <ControlledTextArea
                control={control}
                name='vehicleRating'
                TextAreaProps={{
                  required: true,
                  label: (
                    <Typography style={{ fontWeight: 600 }} component='span'>
                      Vehicle Rating Summary
                    </Typography>
                  ),
                  placeholder: 'Provide your response',
                  invalid: !!getErrorMessage(errors, 'vehicleRating'),
                }}
              />
            </Grid>
          </Grid>
        </div>

        <div className={styles.divider}>
          <Grid container justifyContent='center'>
            <Grid item xs={12} md={8} lg={6}>
              <Divider />
            </Grid>
          </Grid>
        </div>

        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            <div>
              <Grid justifyContent='flex-start' item xs={12} md={10} lg={8}>
                <div className={styles.fieldBox}>
                  <ControlledTextField
                    name='jobTitle'
                    control={control}
                    TextInputProps={{
                      label: (
                        <Typography style={{ fontWeight: 600 }} component='span'>
                          Job Title
                        </Typography>
                      ),
                      error: !!getErrorMessage(errors, 'jobTitle'),
                      placeholder: 'Enter your job title',
                      dataTest: 'job-title-input',
                      large: true,
                      required: true,
                      validationMessage: getErrorMessage(errors, 'jobTitle'),
                    }}
                  />
                </div>
              </Grid>
            </div>
          </Grid>
        </Grid>
        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            <div className={styles.fieldBox}>
              <ControlledTextField
                name='signatory'
                control={control}
                TextInputProps={{
                  label: (
                    <Typography style={{ fontWeight: 600 }} component='span'>
                      Digital Signature
                    </Typography>
                  ),
                  helperMessage: 'Use your full legal name',
                  error: !!getErrorMessage(errors, 'signatory'),
                  placeholder: 'Enter your full name',
                  dataTest: 'signature-input',
                  required: true,
                  large: true,
                  type: 'text',
                  validationMessage: getErrorMessage(errors, 'signatory'),
                }}
              />
            </div>
          </Grid>
        </Grid>
        {errorMessage && (
          <div className={styles.mdGap}>
            <Grid container alignItems='center' justifyContent='center'>
              <Grid item xs={12} md={8} lg={6}>
                <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={errorMessage} />
              </Grid>
            </Grid>
          </div>
        )}

        <div className={styles.mdGap}>
          <Grid container alignItems='center' justifyContent='center'>
            <Grid item xs={12} md={8} lg={6}>
              <div className={styles.lgGap}>
                <Grid container justifyContent='flex-end'>
                  <Button
                    color='tertiary'
                    size='large'
                    data-test='skip-button'
                    onClick={skipHandler}>
                    Skip and Send Later
                  </Button>
                  <Button type='submit' color='primary' size='large' data-test='submit-button'>
                    Submit
                  </Button>
                </Grid>
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
    </form>
  )
}

export default ProvideSafetyLetter
